import React, { useEffect, useState } from "react";
import { THEME, flexxState } from "./state";
import OnboardingFlow from "./OnboardingFlow";
import DiagnosticQuiz from "./DiagnosticQuiz";
import KnowledgeRadar from "./KnowledgeRadar";
import { XPBar } from "./XPSystem";
import { TRACK_LABEL } from "./diagnosticData";
import { MasteryGatesHub } from "./MasteryGate";
import { FlashcardsPanel, useFlashcardStats } from "./FlashcardsPanel";
import AdaptivePractice from "./AdaptivePractice";
import TradingJournal from "./TradingJournal";
import MasteryCapstone from "./MasteryCapstone";
import { PHASES } from "./masteryData";
import CourseProgressRings from "./CourseProgressRings";
import AxelDailyBriefing from "./AxelDailyBriefing";
import ProfilePanel from "./ProfilePanel";
import AiToolsStatus from "./AiToolsStatus";
import CohortChallengeBanner from "./CohortChallengeBanner";
import DailyQuestPanel from "./DailyQuestPanel";

export default function FlexxLearningHub({ user }) {
  const [, setTick] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(!flexxState.isOnboarded());
  const [showDiagnostic, setShowDiagnostic] = useState(
    flexxState.isOnboarded() && !flexxState.isDiagnosticDone()
  );
  const [tab, setTab] = useState(() => {
    // Bottom-tab nav deep-links via URL hash: #journal, #profile, #flashcards, #capstone
    const hash = typeof window !== "undefined" ? window.location.hash.replace("#", "") : "";
    return ["overview", "journal", "profile", "flashcards", "capstone"].includes(hash) ? hash : "overview";
  });
  const [adaptiveOpen, setAdaptiveOpen] = useState(false);

  // Keep tab in sync with URL hash — lets the global bottom nav switch tabs
  useEffect(() => {
    const onHash = () => {
      const h = window.location.hash.replace("#", "");
      if (["overview", "journal", "profile", "flashcards", "capstone"].includes(h)) {
        setTab(h);
      }
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    flexxState.pingStreak();
    const p = flexxState.profile();
    if (!p.name && user?.name) {
      flexxState.saveProfile({ ...p, name: user.name });
    }
  }, [user]);

  // Re-sync onboarding/diagnostic flags after server pull overwrites localStorage
  useEffect(() => {
    const onRestored = () => {
      setShowOnboarding(!flexxState.isOnboarded());
      setShowDiagnostic(flexxState.isOnboarded() && !flexxState.isDiagnosticDone());
      setTick((t) => t + 1);
    };
    window.addEventListener("flexx:restored", onRestored);
    return () => window.removeEventListener("flexx:restored", onRestored);
  }, []);

  const afterOnboarding = () => { setShowOnboarding(false); setShowDiagnostic(true); setTick((t) => t + 1); };
  const afterDiagnostic = () => { setShowDiagnostic(false); setTick((t) => t + 1); };
  const retakeDiagnostic = () => { flexxState.setDiagnosticDone(false); setShowDiagnostic(true); setTick((t) => t + 1); };

  return (
    <>
      {showOnboarding && <OnboardingFlow defaultName={user?.name} onFinish={afterOnboarding} />}
      {showDiagnostic && <DiagnosticQuiz onComplete={afterDiagnostic} />}
      {!showOnboarding && !showDiagnostic && (
        <LearningHubShell
          tab={tab}
          onTab={setTab}
          onAdaptive={() => setAdaptiveOpen(true)}
          onRetakeDiagnostic={retakeDiagnostic}
        />
      )}
      {adaptiveOpen && <AdaptivePractice onClose={() => setAdaptiveOpen(false)} />}
    </>
  );
}

function LearningHubShell({ tab, onTab, onAdaptive, onRetakeDiagnostic }) {
  const profile = flexxState.profile();
  const fc = useFlashcardStats();
  const gates = flexxState.gates();
  const gatesPassed = PHASES.every((p) => gates[p.id]?.passed);
  const capstoneDone = flexxState.capstone().certificateIssued;

  return (
    <div data-testid="flexx-learning-dashboard" className="flexx-fade-in" style={{ padding: "40px 24px 24px", maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>YOUR ACADEMY DASHBOARD</div>
          <h2 style={{ color: THEME.text, fontSize: 26, margin: "6px 0 0" }}>
            Welcome back{profile?.name ? `, ${profile.name}` : ""} 👋
          </h2>
          <p style={{ color: THEME.mute, fontSize: 13, marginTop: 6 }}>
            Trading {profile.market} · {profile.experience} · {profile.studyTime}/day
          </p>
        </div>
        <div style={{ minWidth: 260 }}>
          <XPBar />
        </div>
      </div>

      <div className="flexx-tabnav" role="tablist">
        <button data-testid="tab-overview" className={tab === "overview" ? "active" : ""} onClick={() => onTab("overview")}>Overview</button>
        <button data-testid="tab-flashcards" className={tab === "flashcards" ? "active" : ""} onClick={() => onTab("flashcards")}>
          Flashcards
          {fc.due.length > 0 && <span className={`flexx-badge-dot ${fc.overdue > 0 ? "" : "gold"}`} data-testid="tab-flashcards-badge">{fc.due.length}</span>}
        </button>
        <button data-testid="tab-journal" className={tab === "journal" ? "active" : ""} onClick={() => onTab("journal")}>Journal</button>
        <button
          data-testid="tab-capstone"
          className={tab === "capstone" ? "active" : ""}
          onClick={() => onTab("capstone")}
        >
          Capstone {!gatesPassed && <span style={{ fontSize: 11, opacity: 0.7, marginLeft: 4 }}>🔒</span>}
          {capstoneDone && <span className="flexx-badge-dot gold" data-testid="tab-capstone-badge">🎓</span>}
        </button>
        <button data-testid="tab-profile" className={tab === "profile" ? "active" : ""} onClick={() => onTab("profile")}>Profile</button>
      </div>

      {tab === "overview" && (
        <>
          <AxelDailyBriefing />
          <CohortChallengeBanner />
          <DailyQuestPanel />
          {flexxState.tier2Graduate() && (
            <div style={{ marginTop: 16 }}>
              <AiToolsStatus />
            </div>
          )}
          <OverviewPanel onRetakeDiagnostic={onRetakeDiagnostic} onAdaptive={onAdaptive} />
          <div style={{ marginTop: 16 }}>
            <CourseProgressRings />
          </div>
        </>
      )}
      {tab === "flashcards" && (
        <>
          <FlashcardsPanel />
          <div className="flexx-card" style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>ADAPTIVE PRACTICE</div>
              <div style={{ color: THEME.text, fontWeight: 700, fontSize: 16, marginTop: 4 }}>AXEL Dojo</div>
              <div style={{ color: THEME.mute, fontSize: 12, marginTop: 4 }}>
                Difficulty adjusts to your streak. 3 in a row = level up · 2 wrong = ease back.
              </div>
            </div>
            <button data-testid="open-adaptive-practice-btn" className="flexx-btn-primary" onClick={onAdaptive}>Start Adaptive Practice →</button>
          </div>
        </>
      )}
      {tab === "journal" && <TradingJournal />}
      {tab === "capstone" && <MasteryCapstone />}
      {tab === "profile" && <ProfilePanel />}
    </div>
  );
}

function OverviewPanel({ onRetakeDiagnostic, onAdaptive }) {
  const graph = flexxState.graph();
  const track = flexxState.track();
  const score = flexxState.diagnosticScore();
  const streak = flexxState.streak();
  const gates = flexxState.gates();
  const passed = Object.values(gates).filter((g) => g?.passed).length;
  const label = track ? TRACK_LABEL[track] : null;
  const profile = flexxState.profile();

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 1fr) minmax(280px, 1.2fr) minmax(280px, 1fr)", gap: 16 }} className="flexx-bento-grid">
      {/* LEFT: Learning Profile */}
      <div className="flexx-card" data-testid="dashboard-profile-card">
        <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace", marginBottom: 6 }}>LEARNING PROFILE</div>
        <div style={{ marginBottom: 10 }}>
          {label ? (
            <span className="flexx-chip" style={{ borderColor: `${label.color}80`, background: `${label.color}20`, color: label.color }}>
              {label.name}
            </span>
          ) : (
            <span className="flexx-chip">Unassigned</span>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "center", margin: "6px 0 8px" }}>
          <KnowledgeRadar scores={graph} size={230} />
        </div>
        <div style={{ textAlign: "center", color: THEME.mute, fontSize: 12 }}>
          Diagnostic score: <b style={{ color: THEME.gold }}>{score}%</b>
        </div>
        <button data-testid="retake-diagnostic-btn" className="flexx-btn-ghost" style={{ width: "100%", marginTop: 12, fontSize: 12 }} onClick={onRetakeDiagnostic}>
          Retake Diagnostic
        </button>
      </div>

      {/* CENTER: Mastery Gates */}
      <MasteryGatesHub />

      {/* RIGHT: Streak + Stats + Adaptive shortcut */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="flexx-card" data-testid="dashboard-streak-card">
          <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace", marginBottom: 6 }}>STREAK</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 40 + Math.min(16, streak.days), lineHeight: 1 }}>🔥</div>
            <div>
              <div style={{ color: THEME.text, fontSize: 26, fontWeight: 800 }}>{streak.days}</div>
              <div style={{ color: THEME.mute, fontSize: 12 }}>day{streak.days === 1 ? "" : "s"} in a row</div>
            </div>
          </div>
          <div style={{ color: THEME.mute, fontSize: 11, marginTop: 10, lineHeight: 1.55 }}>
            Most people quit before day 3. Show up daily to stack compounding gains.
          </div>
        </div>

        <div className="flexx-card" data-testid="dashboard-stats-card">
          <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace", marginBottom: 10 }}>ACADEMY STATS</div>
          <StatRow label="Mastery Gates Passed" value={`${passed} / 4`} />
          <StatRow label="Track" value={label?.name || "—"} />
          <StatRow label="Market Focus" value={profile.market} />
          <StatRow label="Study Target" value={`${profile.studyTime}/day`} />
        </div>

        <button data-testid="open-adaptive-practice-shortcut" className="flexx-btn-ghost" style={{ width: "100%" }} onClick={onAdaptive}>
          ⚡ Quick Adaptive Practice
        </button>
      </div>

      <style>{`
        @media (max-width: 900px) { .flexx-bento-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
}

function StatRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px dashed ${THEME.border}` }}>
      <span style={{ color: THEME.mute, fontSize: 12 }}>{label}</span>
      <span style={{ color: THEME.text, fontSize: 13, fontWeight: 600 }}>{value}</span>
    </div>
  );
}
